/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { MouseEvent, ReactNode } from 'react';

import { css, jsx, type CompiledStyles } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { CharlieTableProps } from '../../charlie-table.types';
import { Row } from '../row';

import { calculateRowPadding } from './body.utils';
import { ExpandRowButton } from './expand-row-button';
import type { SharedRowProps } from './shared-row-props.types';

const SubRowExpandableStyle = css({
	left: token('space.0'),
	whiteSpace: 'nowrap',
	zIndex: 100,
	position: 'var(--col-group-position)',
	width: '100%',
	height: '100%',
	paddingBlock: token('space.0'),
});

/** Inline (left/right) borders for grouped row td when shape-theme-default is off. */
const SubRowExpandableInlineBorderStyle = css({
	borderColor: token('color.border'),
	borderInlineWidth: token('border.width'),
	borderInlineStyle: 'solid',
});

/** Block-end (bottom) border for grouped row td. */
const SubRowExpandableBlockEndBorderStyle = css({
	borderColor: token('color.border'),
	borderBottomWidth: token('border.width'),
	borderBottomStyle: 'solid',
});

const groupedRowBackgroundStyles: {
	readonly success: CompiledStyles<{
		backgroundColor: 'var(--ds-background-success)';
	}>;
	readonly danger: CompiledStyles<{
		backgroundColor: 'var(--ds-background-danger)';
	}>;
	readonly information: CompiledStyles<{
		backgroundColor: 'var(--ds-background-information)';
	}>;
	readonly discovery: CompiledStyles<{
		backgroundColor: 'var(--ds-background-discovery)';
	}>;
	readonly warning: CompiledStyles<{
		backgroundColor: 'var(--ds-background-warning)';
	}>;
	readonly selected: CompiledStyles<{
		backgroundColor: 'var(--ds-background-selected)';
	}>;
	readonly disabled: CompiledStyles<{
		// Using 'input.hovered' to avoid any alpha transparency issues with the background color (which affects pinning, for example). Other Neutral100's are too dark in dark mode.
		backgroundColor: 'var(--ds-background-input-hovered)';
	}>;
} = cssMap({
	success: {
		backgroundColor: token('color.background.success'),
	},
	danger: {
		backgroundColor: token('color.background.danger'),
	},
	information: {
		backgroundColor: token('color.background.information'),
	},
	discovery: {
		backgroundColor: token('color.background.discovery'),
	},
	warning: {
		backgroundColor: token('color.background.warning'),
	},
	selected: {
		backgroundColor: token('color.background.selected'),
	},
	disabled: {
		// Using 'input.hovered' to avoid any alpha transparency issues with the background color (which affects pinning, for example). Other Neutral100's are too dark in dark mode.
		backgroundColor: token('color.background.input.hovered'),
	},
});

const styles = cssMap({
	subRowExpandableContainer: {
		height: '100%',
	},
	SubRowButtonWrapperStyle: {
		marginInlineEnd: token('space.050'),
	},
	RowCursorPointer: {
		cursor: 'pointer',
	},
	virtualRow: {
		position: 'absolute',
	},
	SubRowExpandableStyleDefaultRowSize: {
		minHeight: '45px',
	},
	SubRowExpandableStyleCompactRowSize: {
		minHeight: '36px',
	},
});

export type GroupedRowProps<T extends object & { overrideRender?: (rowData: T) => ReactNode }> =
	SharedRowProps<T> & {
		renderChildComponent?: CharlieTableProps<T>['renderChildComponent'];
		groupedRowBackground?: keyof typeof groupedRowBackgroundStyles;
	};

export function GroupedRow<T extends object & { overrideRender?: (rowData: T) => ReactNode }>({
	row,
	table,
	renderOptions,
	renderChildComponent,
	rowSpacing,
	onRowClick,
	groupedRowBackground,
	virtualRow,
	rowIndex,
	rowVirtualizer,
	componentPropOverrides,
	labels,
	interactionName,
	analyticsContext,
}: GroupedRowProps<T>): JSX.Element {
	const shapeThemeRefresh = fg('charlie-table-shape-refresh');

	return (
		<Row
			data-index={rowIndex}
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				...(virtualRow
					? {
							transform: `translateY(${virtualRow.start}px)`,
						}
					: undefined),
			}}
			ref={(node: HTMLTableRowElement | null) =>
				rowVirtualizer ? rowVirtualizer.measureElement(node) : undefined
			}
			spacing={rowSpacing}
			onClick={(e: MouseEvent<HTMLElement>) => {
				// @ts-expect-error Prevent row click if a link was clicked
				if (e.target.closest('a')) {
					return;
				}
				onRowClick?.(row);
			}}
			{...(componentPropOverrides?.row && fg('charly-table-with-row-table-handler')
				? { ...componentPropOverrides.row({ row }) }
				: {})}
			xcss={cx(onRowClick && styles.RowCursorPointer, virtualRow && styles.virtualRow)}
		>
			{/* Not using TD here in order to avoid some overrides we've added */}
			<td
				colSpan={row.getVisibleCells().length}
				css={[
					SubRowExpandableStyle,
					!shapeThemeRefresh && SubRowExpandableInlineBorderStyle,
					SubRowExpandableBlockEndBorderStyle,
					rowSpacing === 'compact'
						? styles.SubRowExpandableStyleCompactRowSize
						: styles.SubRowExpandableStyleDefaultRowSize,
				]}
			>
				<Flex
					alignItems="center"
					xcss={cx(
						styles.subRowExpandableContainer,
						groupedRowBackground && groupedRowBackgroundStyles[groupedRowBackground],
					)}
				>
					{(renderChildComponent || renderOptions?.groupingRow) && row.getCanExpand() && (
						<Box
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- this rule is specific for compiled
								paddingInlineStart: calculateRowPadding(row, 1, renderOptions),
							}}
							xcss={cx(styles.SubRowButtonWrapperStyle)}
						>
							<ExpandRowButton
								row={row}
								interactionName={interactionName}
								analyticsContext={analyticsContext}
								labels={labels}
							/>
						</Box>
					)}
					{row.original.overrideRender?.(row.original)}
					{renderOptions?.groupingRow && renderOptions?.groupingRow(row.original, table.getState())}
				</Flex>
			</td>
		</Row>
	);
}
